import React, { useState, createContext, useContext } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import endPoint from '../service/Service';

import SaveContext from './SaveContext';
import FlowContext from './FlowContext';
import TestCaseDTO from '../dto/TestCaseDTO';

export const Context = createContext({
  fetchTestCases: () => {},
  testCases: [],
  createTestCase: () => {},
  updateTestCase: () => {},
  removeTestCase: () => {},
  fetchTestCasesSuites: () => {},
  createTestCaseSuite: () => {},
  testCasesSuites: [],
  changeCurrentTestCaseSuite: () => {},
  deleteCurrentTestCaseSuite: () => {},
  getTestCasesCopy: () => {},
});

export const ContextProviderChatbotTesting = ({ children }) => {
  const saveContext = useContext(SaveContext);
  const flowContext = useContext(FlowContext);
  const [testCases, setTestCases] = useState([]);
  const [testCasesSuites, setTestCasesSuites] = useState([]);

  const changeCurrentTestCaseSuite = (suiteName) => {
    const testCasesSuitesUpdated = testCasesSuites.map((s) => ({ ...s, current: undefined }));
    const idx = testCasesSuitesUpdated.findIndex((t) => t.name === suiteName);
    testCasesSuitesUpdated[idx].current = true;
    setTestCasesSuites(testCasesSuitesUpdated);
  };

  const fetchTestCasesSuites = () => {
    setTestCasesSuites([]);
    const flowId = flowContext.flow.id;
    endPoint.get(`flows/${flowId}/testcasesuites/`).then(
      (res) => {
        if (res.status === 200) {
          if (res.data.length > 0) {
            const suites = res.data;
            suites[0].current = true;
            setTestCasesSuites(suites);
          }
        }
      },
    );
  };

  const fetchTestCases = () => {
    setTestCases([]);
    const flowId = flowContext.flow.id;
    const currentSuite = testCasesSuites.find((suite) => suite.current);
    if (!currentSuite) return;
    endPoint.get(`flows/${flowId}/testcasesuites/${currentSuite.id}/testcases/`).then(
      (res) => {
        if (res.status === 200) {
          if (res.data.length > 0) {
            setTestCases(res.data.map((i) => new TestCaseDTO(i)));
          }
        }
      },
    );
  };

  const createTestCaseSuite = (name, successCallBack) => {
    const flowId = flowContext.flow.id;
    const payload = {
      name,
    };

    saveContext.beginSaving();
    endPoint.post(`flows/${flowId}/testcasesuites`, payload).then(
      async (res) => {
        if (res.status === 201) {
          const testCasesSuitesUpdated = testCasesSuites.map((t) => ({ ...t, current: undefined }));
          testCasesSuitesUpdated.push({ ...res.data, current: true });
          const newTestCase = new TestCaseDTO();
          newTestCase.suite = res.data.id;

          await endPoint.post(`flows/${flowId}/testcases`, newTestCase.mapToResquest()).then((response) => {
            if (response.status === 201) {
              setTestCasesSuites(testCasesSuitesUpdated);
              saveContext.finishSaving();
              successCallBack();
            }
          });
        }
      },
    );
  };

  const deleteCurrentTestCaseSuite = () => {
    const flowId = flowContext.flow.id;
    const currentSuite = testCasesSuites.find((suite) => suite.current);
    if (!currentSuite) return null;
    saveContext.beginSaving();
    endPoint.delete(`flows/${flowId}/testcasesuites/${currentSuite.id}`).then(
      (res) => {
        if (res.status === 204) {
          const updatedTestCases = [...testCasesSuites.filter((i) => i.id !== currentSuite.id)];
          if (updatedTestCases.length > 0) {
            updatedTestCases[0].current = true;
          }
          setTestCasesSuites(updatedTestCases);
        }
        saveContext.finishSaving();
      },
    );
  };

  const createTestCase = () => {
    const newTestCase = new TestCaseDTO();
    const currentSuite = testCasesSuites.find((suite) => suite.current);
    if (currentSuite) {
      newTestCase.suite = currentSuite.id;
    }

    const flowId = flowContext.flow.id;
    saveContext.beginSaving();
    endPoint.post(`flows/${flowId}/testcases`, newTestCase.mapToResquest()).then(
      (res) => {
        if (res.status === 201) {
          setTestCases(
            [...testCases,
              new TestCaseDTO(res.data),
            ],
          );
          saveContext.finishSaving();
        }
      },
    );
  };

  const testCaseEquals = (a, b) => {
    const compA = Object.assign(new TestCaseDTO(), a);
    delete compA.status;
    delete compA.result;
    const compB = Object.assign(new TestCaseDTO(), b);
    delete compB.status;
    delete compA.result;
    return JSON.stringify(compA) === JSON.stringify(compB);
  };

  const updateTestCase = (testCase, successCallBack = null) => {
    const flowId = flowContext.flow.id;
    const currentSuite = testCasesSuites.find((suite) => suite.current);

    if (!currentSuite) return;
    if (testCaseEquals(testCase, testCases.find((i) => i.id === testCase.id))) return;

    saveContext.beginSaving();
    endPoint.put(`flows/${flowId}/testcasesuites/${currentSuite.id}/testcases/${testCase.id}`, testCase.mapToResquest()).then(
      (res) => {
        if (res.status === 200) {
          const idx = testCases.findIndex((i) => i.id === res.data.id);
          testCases[idx] = new TestCaseDTO(res.data);
          setTestCases([...testCases]);
          if (successCallBack) successCallBack();
          saveContext.finishSaving();
        }
      },
    );
  };

  const removeTestCase = (testCase) => {
    const flowId = flowContext.flow.id;
    const currentSuite = testCasesSuites.find((suite) => suite.current);
    if (!currentSuite) return;
    saveContext.beginSaving();
    endPoint.delete(`flows/${flowId}/testcasesuites/${currentSuite.id}/testcases/${testCase.id}`).then(
      (res) => {
        if (res.status === 204) {
          setTestCases([...testCases.filter((i) => i.id !== testCase.id)]);
        }
        saveContext.finishSaving();
      },
    );
  };

  const getTestCasesCopy = () => cloneDeep(testCases);

  return (
    <Context.Provider value={{
      fetchTestCases,
      testCases,
      createTestCase,
      updateTestCase,
      removeTestCase,
      fetchTestCasesSuites,
      testCasesSuites,
      createTestCaseSuite,
      changeCurrentTestCaseSuite,
      deleteCurrentTestCaseSuite,
      getTestCasesCopy,
    }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
